import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nContext';

const Icon = ({ name, className }) => {
    const map = {
        'truck': 'bi bi-truck',
        'person': 'bi bi-person-badge',
        'camera': 'bi bi-camera',
        'ticket': 'bi bi-ticket-perforated',
        'search': 'bi bi-search',
        'shield': 'bi bi-shield-check',
        'warning': 'bi bi-exclamation-triangle',
        'spinner': 'bi bi-arrow-repeat spinning',
        'check': 'bi bi-check-circle-fill',
        'x': 'bi bi-x-circle-fill',
        'alert': 'bi bi-exclamation-triangle-fill',
        'info': 'bi bi-info-circle-fill',
        'bell': 'bi bi-bell-fill',
        'chat': 'bi bi-chat-left-text-fill'
    };
    return <i className={`${map[name] || `bi bi-${name}`} ${className || ''}`} />;
};

const FraudTab = () => {
    const { t } = useI18n();
    const [missions, setMissions] = useState([]);
    const [selectedMission, setSelectedMission] = useState('');
    const [missionContext, setMissionContext] = useState(null);
    
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [fraudResults, setFraudResults] = useState(null);

    const getMissionLabel = (m) => {
        if (!m) return '';
        const departureStr = m.expediteur_nom || m.expediteurNom || m.departure_location || m.departureLocation;
        const arrivalStr = m.destinataire_nom || m.destinataireNom || m.arrival_location || m.arrivalLocation;
        let label = `${departureStr || '?'} → ${arrivalStr || '?'}`;
        if (m.expediteur_nom || m.expediteurNom || m.destinataire_nom || m.destinataireNom) {
            label += ` (${m.departure_location || m.departureLocation || '?'} → ${m.arrival_location || m.arrivalLocation || '?'})`;
        }
        return label;
    };

    useEffect(() => {
        fetchMissions();
    }, []);

    const fetchMissions = async () => {
        try {
            const res = await api.get('/agent/planning/fraud/pricing/missions?mode=all');
            let allMissions = [];
            if (Array.isArray(res.data)) {
                allMissions = res.data;
            } else if (res.data?.data?.missions) {
                allMissions = res.data.data.missions;
            } else if (res.data?.missions) {
                allMissions = res.data.missions;
            } else if (res.data?.data && Array.isArray(res.data.data)) {
                allMissions = res.data.data;
            }

            const filteredMissions = allMissions.filter(m => {
                const status = (m.status || '').toUpperCase();
                return status.includes('COUR') || status.includes('TERMIN');
            });

            setMissions(filteredMissions);
        } catch (e) {
            console.error("Failed to fetch missions", e);
            setError("Impossible de charger la liste des missions.");
        }
    };

    const handleMissionChange = async (mid) => {
        setSelectedMission(mid);
        setMissionContext(null);
        if (!mid) return;
        try {
            const res = await api.get(`/agent/planning/fraud/missions/${mid}/context`);
            setMissionContext(res.data);
        } catch (e) {
            console.error("Failed to fetch mission context", e);
        }
    };

    const runAnalysis = async () => {
        if (!selectedMission) return;
        setLoading(true);
        setResult(null);
        setError(null);
        
        const fd = new FormData();
        fd.append('mission_id', selectedMission);

        try {
            const res = await api.post('/agent/planning/fraud/analyze', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResult(res.data);
        } catch (e) {
            console.error("Analysis error", e);
            const serverError = e.response?.data?.error || e.response?.data?.message || e.message;
            setError(serverError ? `Erreur: ${serverError}` : "Erreur lors de l'analyse. Vérifiez que l'agent IA est en ligne.");
        } finally {
            setLoading(false);
        }
    };

    const renderResultSection = () => {
        if (!result) return null;

        // Safely extract score, anomalies, justification, notifications
        const full = result.full_analysis || {};
        const rawRes = full.result || {};

        let score = 0;
        if (typeof rawRes.fraudScore !== 'undefined') {
            score = Number(rawRes.fraudScore);
        } else if (typeof full.fraudScore !== 'undefined') {
            score = Number(full.fraudScore);
        } else if (result.details?.Score_Fraude) {
            score = Number(result.details.Score_Fraude.replace('/100', ''));
        }

        let anomalies = [];
        if (Array.isArray(rawRes.anomalies)) {
            anomalies = rawRes.anomalies;
        } else if (Array.isArray(full.anomalies)) {
            anomalies = full.anomalies;
        }

        const justification = rawRes.justification || full.justification || result.text || "Aucune justification fournie.";
        const notification = rawRes.notificationMessage || full.notificationMessage || "Plusieurs anomalies ont été détectées. Veuillez vérifier les données et les entrées manuelles.";

        // Determine severity tier
        let severityBadge = (
            <span className="badge bg-success-soft text-success border border-success-subtle rounded-pill py-2 px-3 fw-bold">
                Conforme
            </span>
        );
        if (score > 70) {
            severityBadge = (
                <span className="badge bg-danger-soft text-danger border border-danger-subtle rounded-pill py-2 px-3 fw-bold">
                    Alerte élevée
                </span>
            );
        } else if (score > 35) {
            severityBadge = (
                <span className="badge bg-warning-soft text-warning border border-warning-subtle rounded-pill py-2 px-3 fw-bold">
                    Alerte modérée
                </span>
            );
        }

        return (
            <div className="analysis-result-section mt-4 fade-in">
                {/* 1. Score Header Card */}
                <div className="fraud-result-card border shadow-sm rounded-4 p-4 mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div>
                            <div className="text-muted small fw-semibold text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>
                                Score de fraude
                            </div>
                            <div className="h1 fw-extrabold text-dark mb-1" style={{ fontSize: '3rem' }}>
                                {score.toFixed(2)}
                            </div>
                            <p className="text-muted small mb-0">
                                Lecture structurée du niveau de fraude détecté par l'analyse.
                            </p>
                        </div>
                        <div>
                            {severityBadge}
                        </div>
                    </div>
                </div>

                {/* 2. Three-Column Grid */}
                <div className="row g-4">
                    {/* Column 1: Anomalies détectées */}
                    <div className="col-lg-5 col-md-12">
                        <div className="fraud-result-card border shadow-sm rounded-4 p-4 h-100">
                            <h5 className="fw-bold mb-4 text-dark d-flex align-items-center gap-2">
                                <Icon name="warning" className="text-danger" /> Anomalies détectées
                            </h5>
                            
                            <div className="anomalies-list-scroll overflow-auto pr-1" style={{ maxHeight: '550px' }}>
                                {anomalies.length === 0 ? (
                                    <div className="p-4 bg-success-soft border border-success-subtle rounded-4 text-center text-success small">
                                        <Icon name="check" className="fs-3 mb-2 d-block" />
                                        Aucune anomalie critique détectée lors de l'analyse.
                                    </div>
                                ) : (
                                    anomalies.map((a, idx) => {
                                        let severityColor = "bg-primary-soft text-primary";
                                        if (a.severity === "HIGH") severityColor = "bg-danger-soft text-danger border border-danger-subtle";
                                        else if (a.severity === "MEDIUM") severityColor = "bg-warning-soft text-warning border border-warning-subtle";

                                        return (
                                            <div key={idx} className="p-3 bg-light border rounded-3 mb-3 shadow-sm position-relative">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="fw-bold text-dark small text-break" style={{ fontSize: '0.85rem' }}>
                                                        {a.code || a.type || 'ANOMALIE'}
                                                    </span>
                                                    <span className={`badge rounded-pill extra-small px-2 py-1 ${severityColor}`} style={{ fontSize: '0.65rem' }}>
                                                        {a.severity || 'LOW'}
                                                    </span>
                                                </div>
                                                <p className="text-muted mb-2 text-break" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                                    {a.message || a.description || 'Aucune description.'}
                                                </p>
                                                <div className="text-muted extra-small text-uppercase fw-semibold text-break" style={{ fontSize: '0.68rem', letterSpacing: '0.02em' }}>
                                                    {a.code || 'UNKNOWN'}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Column 2: Notifications */}
                    <div className="col-lg-3 col-md-6">
                        <div className="fraud-result-card border shadow-sm rounded-4 p-4 h-100">
                            <h5 className="fw-bold mb-4 text-dark d-flex align-items-center gap-2">
                                <Icon name="bell" className="text-warning" /> Notifications
                            </h5>

                            {score <= 35 ? (
                                <div className="p-3 bg-light border rounded-4 text-muted text-center small">
                                    Aucune notification.
                                </div>
                            ) : (
                                <div className="p-3 bg-light border rounded-3 shadow-sm border-warning border-opacity-25">
                                    <div className="fw-bold text-dark small mb-1">
                                        Notification 1
                                    </div>
                                    <p className="text-muted small mb-0 text-break" style={{ lineHeight: '1.4' }}>
                                        {notification}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Column 3: Justification */}
                    <div className="col-lg-4 col-md-6">
                        <div className="fraud-result-card border shadow-sm rounded-4 p-4 h-100">
                            <h5 className="fw-bold mb-4 text-dark d-flex align-items-center gap-2">
                                <Icon name="chat" className="text-primary" /> Justification
                            </h5>

                            <div className="p-3 bg-light border rounded-3 shadow-sm h-100">
                                <p className="text-dark mb-0 small text-break" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {justification}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Collapsible raw details */}
                <details className="mt-4 pt-3 border-top">
                    <summary className="text-muted small fw-bold cursor-pointer" style={{ listStyle: 'none', cursor: 'pointer' }}>
                        ▶ Détail technique complet
                    </summary>
                    <pre className="bg-light p-3 rounded-3 mt-2 text-dark border overflow-auto text-break" style={{ maxHeight: '250px', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </details>
            </div>
        );
    };

    return (
        <div className="fraud-tab fade-in">
            <div className="acc-card border shadow-sm rounded-4 p-4 mb-4">
                <div className="acc-card-title mb-4">
                    <Icon name="shield" /> {t('agentCenter.tabs.fraud')}
                </div>
                
                <div className="row g-4">
                    <div className="col-md-12">
                        <div className="acc-field-group mb-3">
                            <label><Icon name="truck" /> Mission</label>
                            <select className="form-select py-2 px-3 border rounded-3" value={selectedMission} onChange={e => handleMissionChange(e.target.value)}>
                                <option value="">— Sélectionner une mission —</option>
                                {missions.map(m => (
                                    <option key={m.mission_id} value={m.mission_id}>
                                        {getMissionLabel(m)} ({m.status})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {missionContext && (
                    <div className="p-3 bg-light rounded-3 mb-4 border shadow-sm mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="small fw-bold text-muted text-uppercase">Contexte de la Mission</div>
                            <div className="d-flex gap-2">
                                {missionContext.has_ticket && <span className="badge bg-success-subtle text-success border border-success-subtle"><Icon name="check" /> Ticket</span>}
                                {missionContext.has_cuvette && <span className="badge bg-success-subtle text-success border border-success-subtle"><Icon name="check" /> Cuvette</span>}
                                {missionContext.has_jauge && <span className="badge bg-success-subtle text-success border border-success-subtle"><Icon name="check" /> Jauge</span>}
                            </div>
                        </div>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <div className="small text-muted">Véhicule</div>
                                <div className="fw-semibold">
                                    {missionContext.vehicule ? (missionContext.vehicule.brand || missionContext.vehicule.marque || '') + ' - ' + (missionContext.vehicule.plateNumber || missionContext.vehicule.plate_number || missionContext.vehicule.plaque || '') : 'Non assigné'}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="small text-muted">Chauffeur</div>
                                <div className="fw-semibold">
                                    {missionContext.chauffeur ? (missionContext.chauffeur.firstName || missionContext.chauffeur.nom || '') + ' ' + (missionContext.chauffeur.lastName || missionContext.chauffeur.prenom || '') : 'Non assigné'}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="small text-muted">Trajet</div>
                                <div className="fw-semibold">{missionContext.mission ? (missionContext.mission.departureLocation || missionContext.mission.departure_location) + ' → ' + (missionContext.mission.arrivalLocation || missionContext.mission.arrival_location) : missionContext.trajet}</div>
                            </div>
                        </div>
                        {(missionContext.has_ticket || missionContext.has_cuvette || missionContext.has_jauge) && (
                            <div className="mt-3 pt-3 border-top text-success small fw-medium">
                                <Icon name="shield" /> Des photos sont déjà associées à cette mission (via l'application mobile).
                            </div>
                        )}
                    </div>
                )}

                <button 
                    className="acc-btn-primary w-100 py-2.5 rounded-3 fw-semibold mt-3"
                    onClick={runAnalysis} 
                    disabled={!selectedMission || loading}
                >
                    {loading ? (
                        <><Icon name="spinner" /> Analyse en cours...</>
                    ) : (
                        <><Icon name="shield" /> { (missionContext?.has_ticket || missionContext?.has_cuvette || missionContext?.has_jauge) ? "Lancer l'analyse (images existantes)" : "Lancer l'analyse de fraude" }</>
                    )}
                </button>

                {error && <div className="alert alert-danger mt-4"><Icon name="warning" /> {error}</div>}
            </div>

            {renderResultSection()}

            <style dangerouslySetInnerHTML={{ __html: `
                .extra-small { font-size: 0.72rem; }
                .cursor-pointer { cursor: pointer; }
                .bg-light { background-color: #f8f9fa !important; }
                .fraud-result-card {
                    background: var(--acc-surface) !important;
                    border: 1px solid var(--acc-border) !important;
                    border-radius: 1rem !important;
                    padding: 1.5rem !important;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
                    display: flex;
                    flex-direction: column;
                }
                .anomalies-list-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .anomalies-list-scroll::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .anomalies-list-scroll::-webkit-scrollbar-thumb {
                    background: #ccc;
                    border-radius: 4px;
                }
                .anomalies-list-scroll::-webkit-scrollbar-thumb:hover {
                    background: #999;
                }
            `}} />
        </div>
    );
};

export default FraudTab;
